import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, useSensor, useSensors, MouseSensor, TouchSensor } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { useTripContext } from './TripDetailPage';
import { tipsApi } from '../../api/tips.api';
import { aiApi } from '../../api/ai.api';
import { useAiDataVersion, useAiPanel, useAiPanelState } from '../../components/ai/AiPanelContext';
import PackingSectionCard, { type SectionMeta } from '../../components/trips/packing/PackingSectionCard';
import PackingScopeBar, { type ScopeTab } from '../../components/trips/packing/PackingScopeBar';
import PackingSuggestions from '../../components/trips/packing/PackingSuggestions';
import PackingCopyModal from '../../components/trips/packing/PackingCopyModal';
import OutfitPlanner from '../../components/trips/packing/outfits/OutfitPlanner';
import type { PackingMember } from '../../components/trips/packing/PackingItemRow';
import { useAuthStore } from '../../stores/auth.store';
import { tripMembers, tripAccountMembers } from '../../utils/tripMembers';
import type { AiPersistedAction } from '../../api/aiProposals';
import type { ChecklistSection, ChecklistItem, PackingScope, PackingCopyResult } from '../../types';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { toast } from '../../components/ui/Toast';
import { isAlreadyGone } from '../../api/client';
import {
  Plus, Copy, FileText, Shirt, SprayCan, Zap, Pill, Ticket, Wallet,
  ListChecks, Lock, Icon,
} from '../../components/ui/Icon';

// Fixed 1:1 with packing.defaultSectionNames (translated) — Documents,
// Clothing, Toiletries, Electronics, Medications, Reservations, Money in
// that order. Matched by exact translated name rather than fuzzy keyword
// substrings: section titles are free text in any of 5 locales, and
// substring matching across languages (e.g. "electronic" inside accented
// "electrónica") is too fragile. Anything that isn't one of the defaults
// falls back to a cycling palette — same spirit as TIP_COLORS in
// InfoTab.tsx for tip categories, which are free text too.
const DEFAULT_SECTION_META: SectionMeta[] = [
  { Icon: FileText, color: 'text-sky-600',    bg: 'bg-sky-50',    accent: '#0284c7' },
  { Icon: Shirt,    color: 'text-violet-600', bg: 'bg-violet-50', accent: '#7c3aed' },
  { Icon: SprayCan, color: 'text-cyan-600',   bg: 'bg-cyan-50',   accent: '#0891b2' },
  { Icon: Zap,      color: 'text-amber-600',  bg: 'bg-amber-50',  accent: '#d97706' },
  { Icon: Pill,     color: 'text-rose-600',   bg: 'bg-rose-50',   accent: '#e11d48' },
  { Icon: Ticket,   color: 'text-teal-600',   bg: 'bg-teal-50',   accent: '#0d9488' },
  { Icon: Wallet,   color: 'text-green-600',  bg: 'bg-green-50',  accent: '#16a34a' },
];
const SECTION_FALLBACK_PALETTE: { color: string; bg: string; accent: string }[] = [
  { color: 'text-slate-500',  bg: 'bg-slate-100',  accent: '#64748b' },
  { color: 'text-pink-600',   bg: 'bg-pink-50',    accent: '#db2777' },
  { color: 'text-indigo-600', bg: 'bg-indigo-50',  accent: '#4f46e5' },
];
function getSectionMeta(title: string, index: number, defaultNames: string[]): SectionMeta {
  const lower = title.trim().toLowerCase();
  const matchIdx = defaultNames.findIndex(n => n.toLowerCase() === lower);
  if (matchIdx !== -1) return DEFAULT_SECTION_META[matchIdx];
  return { Icon: ListChecks, ...SECTION_FALLBACK_PALETTE[index % SECTION_FALLBACK_PALETTE.length] };
}

const SCOPE_MINE   = 'mine';
const SCOPE_COMMON = 'common';

export default function PackingTab() {
  const { trip, days } = useTripContext();
  const { t } = useTranslation();
  // The trip's language, not the reader's: these names are seeded into the trip
  // and shown as quick-add chips, so whoever opens the tab first would otherwise
  // decide the language of everyone's packing list.
  const defaultSectionNames = t('packing.defaultSectionNames', {
    returnObjects: true, lng: trip.contentLanguage,
  }) as string[];
  const [checklists, setChecklists] = useState<ChecklistSection[]>([]);
  const [loading, setLoading]       = useState(true);

  const me = useAuthStore(s => s.user);
  const myId = me?.id ?? '';
  // A solo trip has nothing to separate: showing one person a "mine / shared"
  // switch is pure noise, so the whole scope UI only appears from two up.
  const isGroup = tripAccountMembers(trip).length > 1;
  const [scope, setScope] = useState<string>(SCOPE_MINE);
  const [sharesPacking, setSharesPacking] = useState(true);

  // Packing proposals accepted in the docked gIA panel land here.
  useAiPanel({ context: 'packing', onAction: handlePackingAiAction });
  // Drop to 2 columns while the docked panel narrows the content (see VenuesTab).
  const { collapsed: aiCollapsed } = useAiPanelState();
  function handlePackingAiAction(actions: AiPersistedAction[]) {
    for (const a of actions) {
      if (a.type === 'packing_sections_created') setChecklists(c => [...c, ...a.data]);
      // Items landed in sections already on screen: reload rather than patch each one.
      if (a.type === 'packing_items_updated') tipsApi.listChecklists(trip.id).then(setChecklists).catch(() => {});
    }
  }

  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);

  // Per-section AI generation — separate from the page-level AI panel above:
  // scoped to one existing section, auto-saves straight into it (no proposal
  // preview step), since the section itself is already the user's confirmed
  // intent to pack for that category.
  const [sectionAiOpenId, setSectionAiOpenId]         = useState<string | null>(null);
  const [generatingSectionId, setGeneratingSectionId] = useState<string | null>(null);

  // Touch: press-and-hold to drag so a finger swipe scrolls the grid instead of
  // being eaten by an accidental reorder (same rationale as ItineraryTab).
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  useEffect(() => {
    Promise.all([tipsApi.listChecklists(trip.id), tipsApi.getPackingSettings(trip.id)])
      .then(async ([cl, settings]) => {
        setSharesPacking(settings.sharesPacking);
        // Seeded when YOU have no suitcase yet, not when the TRIP has no
        // sections: on a group trip the list is already full of other people's
        // when you arrive, and the old "is it empty" check would have left
        // whoever joined second with nothing and no way to seed it.
        //
        // The server still decides whether it actually happens — a conditional
        // update on your own participant row is the compare-and-swap, so two
        // concurrent calls (React StrictMode's double effect fire in dev) can't
        // both create a batch, deleting your sections afterwards sticks, and a
        // trip seeded before per-person packing existed is left alone.
        if (cl.some(sec => sec.ownerUserId === myId)) { setChecklists(cl); return; }
        const { sections } = await tipsApi.seedDefaultSections(trip.id, defaultSectionNames);
        setChecklists([...cl, ...sections]);
      })
      .catch(() => toast(t('packing.loadFailed'), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip.id]);

  // ADR-0028: re-read when an accepted card changed a section or an item. Apart
  // from the first load, which also seeds your suitcase.
  const aiPackingVersion = useAiDataVersion('packing');
  useEffect(() => {
    if (aiPackingVersion === 0) return;
    tipsApi.listChecklists(trip.id).then(setChecklists).catch(() => {});
  }, [aiPackingVersion, trip.id]);

  // Derived, not stored: the tabs are the shapes the fetched sections already
  // have, so they cannot drift out of sync with what is on screen.
  const memberLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of tripMembers(trip)) map.set(p.userId, p.user.displayName || p.user.username);
    return map;
  }, [trip]);

  const visibleSections = useMemo(() => {
    // Solo trip: no tabs, so no filter either — it is all yours anyway.
    if (!isGroup) return checklists;
    if (scope === SCOPE_COMMON) return checklists.filter(sec => sec.ownerUserId === null);
    if (scope === SCOPE_MINE)   return checklists.filter(sec => sec.ownerUserId === myId);
    return checklists.filter(sec => sec.ownerUserId === scope);
  }, [checklists, scope, isGroup, myId]);

  const tabs: ScopeTab[] = useMemo(() => {
    const count = (fn: (s: ChecklistSection) => boolean) => checklists.filter(fn).length;
    const others = [...new Set(
      checklists.map(sec => sec.ownerUserId).filter((id): id is string => !!id && id !== myId),
    )];
    return [
      { key: SCOPE_MINE,   label: t('packing.scope.mine'),   count: count(sec => sec.ownerUserId === myId) },
      { key: SCOPE_COMMON, label: t('packing.scope.common'), count: count(sec => sec.ownerUserId === null) },
      ...others.map(id => ({
        key: id,
        label: memberLabels.get(id) ?? t('packing.scope.someone'),
        count: count(sec => sec.ownerUserId === id),
      })),
    ];
  }, [checklists, myId, memberLabels, t]);

  // Reading is allowed, writing is not: "you forgot the charger" is what the
  // shared section is for, not editing someone else's list.
  const readOnly = isGroup && scope !== SCOPE_MINE && scope !== SCOPE_COMMON;
  const newSectionScope: PackingScope = scope === SCOPE_COMMON ? 'common' : 'personal';

  // ADR-0017: clothes per day are personal, so they sit under a person's tab
  // (yours, or a sharer's read-only) and never under the shared one.
  const showOutfits = !isGroup || scope !== SCOPE_COMMON;
  const outfitOwnerId = isGroup && readOnly ? scope : myId;
  const itemsById = useMemo(
    () => new Map(checklists.flatMap(sec => sec.items.map(i => [i.id, i] as const))),
    [checklists],
  );
  // ADR-0018: alone, the shared sections are your suitcase too — the tab shows
  // them as one list, and pre-per-person trips keep all their clothes there.
  const mySections = useMemo(
    () => checklists.filter(sec => sec.ownerUserId === myId || (!isGroup && sec.ownerUserId === null)),
    [checklists, myId, isGroup],
  );
  const clothingName = defaultSectionNames[1].trim().toLowerCase();
  const myClothingSection = mySections.find(sec => sec.title.trim().toLowerCase() === clothingName) ?? null;

  /**
   * A garment written from a day goes into the section picked for it, or, with
   * none picked, into your clothing section (by its default name in the trip's
   * language, created if you deleted it), so what you plan to wear is also on
   * the list of what to pack.
   */
  async function createGarment(text: string, sectionId: string | null): Promise<ChecklistItem | null> {
    try {
      let targetId = sectionId ?? myClothingSection?.id;
      if (!targetId) {
        const created = await tipsApi.createSection(trip.id, { title: defaultSectionNames[1], scope: 'personal' });
        setChecklists(c => [...c, created]);
        targetId = created.id;
      }
      const item = await tipsApi.createItem(trip.id, targetId, { text });
      setChecklists(c => c.map(sec => sec.id === targetId ? { ...sec, items: [...sec.items, item] } : sec));
      return item;
    } catch {
      toast(t('packing.outfits.addFailed'), 'error');
      return null;
    }
  }

  const assignableMembers: PackingMember[] = useMemo(
    () => tripMembers(trip).map(p => ({ id: p.userId, label: p.user.displayName || p.user.username })),
    [trip],
  );

  async function toggleSharing() {
    const next = !sharesPacking;
    setSharesPacking(next);
    try {
      await tipsApi.updatePackingSettings(trip.id, next);
      toast(next ? t('packing.sharing.nowShared') : t('packing.sharing.nowPrivate'), 'success');
    } catch {
      setSharesPacking(!next);
      toast(t('packing.sharing.failed'), 'error');
    }
  }

  async function assignItem(sectionId: string, itemId: string, userId: string | null) {
    const updated = await tipsApi.updateItem(trip.id, sectionId, itemId, { assignedToUserId: userId });
    setChecklists(c => c.map(sec =>
      sec.id === sectionId ? { ...sec, items: sec.items.map(i => i.id === itemId ? updated : i) } : sec,
    ));
  }

  // A new section belongs to whatever the tabs are showing, so there is no
  // extra "is this mine or ours" field to fill in on every add.
  //
  // Sequential rather than Promise.all: the server derives each sortOrder from
  // the last one it can see, so firing them at once would hand several sections
  // the same position.
  async function addSections(titles: string[]) {
    const created: ChecklistSection[] = [];
    try {
      for (const title of titles) {
        created.push(await tipsApi.createSection(trip.id, { title, scope: newSectionScope }));
      }
    } catch {
      toast(t('packing.addSectionFailed'), 'error');
    }
    if (created.length > 0) setChecklists(c => [...c, ...created]);
  }

  /**
   * Copies land as whole sections, created or merged into by the server, so the
   * result is applied by id: an existing section is replaced in place (keeping
   * its position on screen) and a new one appends.
   */
  function applyCopy(result: PackingCopyResult) {
    if (result.addedItems === 0) { toast(t('packing.copy.nothingNew'), 'info'); return; }
    setChecklists(c => {
      const byId = new Map(c.map(s => [s.id, s]));
      for (const s of result.sections) byId.set(s.id, s);
      return [...byId.values()];
    });
    toast(t('packing.copy.done', { count: result.addedItems }), 'success');
  }

  async function addSection() {
    const title = newSectionTitle.trim();
    if (!title) return;
    await addSections([title]);
    setNewSectionTitle('');
  }

  async function deleteSection(sectionId: string) {
    try {
      await tipsApi.deleteSection(trip.id, sectionId);
    } catch (e) {
      if (!isAlreadyGone(e)) { toast(t('common.error'), 'error'); return; }
    }
    setChecklists(c => c.filter(s => s.id !== sectionId));
    toast(t('packing.sectionRemoved'), 'success');
  }

  async function toggleItem(sectionId: string, itemId: string, checked: boolean) {
    const updated = await tipsApi.updateItem(trip.id, sectionId, itemId, { checked });
    setChecklists(c => c.map(s =>
      s.id === sectionId ? { ...s, items: s.items.map(i => i.id === itemId ? updated : i) } : s,
    ));
  }

  /**
   * Optimistic, unlike the other item writes: the text is what the user is
   * looking at, and flashing back to the old wording while the PATCH lands
   * reads as if the edit had been rejected.
   */
  async function renameItem(sectionId: string, itemId: string, text: string) {
    const previous = checklists;
    setChecklists(c => c.map(s =>
      s.id === sectionId ? { ...s, items: s.items.map(i => i.id === itemId ? { ...i, text } : i) } : s,
    ));
    try {
      const updated = await tipsApi.updateItem(trip.id, sectionId, itemId, { text });
      setChecklists(c => c.map(s =>
        s.id === sectionId ? { ...s, items: s.items.map(i => i.id === itemId ? updated : i) } : s,
      ));
    } catch {
      setChecklists(previous);
      toast(t('packing.renameItemFailed'), 'error');
    }
  }

  async function addItem(sectionId: string, text: string) {
    const item = await tipsApi.createItem(trip.id, sectionId, { text });
    setChecklists(c => c.map(s =>
      s.id === sectionId ? { ...s, items: [...s.items, item] } : s,
    ));
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIdx = checklists.findIndex(s => s.id === active.id);
    const newIdx = checklists.findIndex(s => s.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;

    const previous  = checklists;
    const reordered = arrayMove(checklists, oldIdx, newIdx);
    setChecklists(reordered);
    try {
      await tipsApi.reorderSections(trip.id, reordered.map(s => s.id));
    } catch {
      setChecklists(previous);
      toast(t('packing.reorderFailed'), 'error');
    }
  }

  async function deleteItem(sectionId: string, itemId: string) {
    try {
      await tipsApi.deleteItem(trip.id, sectionId, itemId);
    } catch (e) {
      if (!isAlreadyGone(e)) { toast(t('common.error'), 'error'); return; }
    }
    setChecklists(c => c.map(s =>
      s.id === sectionId ? { ...s, items: s.items.filter(i => i.id !== itemId) } : s,
    ));
  }

  async function handleSectionAi(section: ChecklistSection, info: string): Promise<boolean> {
    setGeneratingSectionId(section.id);
    try {
      const prompt = t('packing.sectionAiPrompt', {
        title:    section.title,
        info:     info || t('packing.sectionAiNoExtraInfo'),
        existing: section.items.map(i => i.text).join(', ') || t('packing.sectionAiNoExistingItems'),
      });
      const res = await aiApi.chat(trip.id, [{ role: 'user', content: prompt }], 'packing');
      const proposalAction = res.actions.find(a => a.type === 'packing_proposed');
      const targetTitle = section.title.trim().toLowerCase();
      const matched = proposalAction?.data.sections.find(s => s.title.trim().toLowerCase() === targetTitle)
        ?? proposalAction?.data.sections[0];

      if (!matched || matched.items.length === 0) {
        toast(t('packing.sectionAiEmpty'), 'error');
        return false;
      }

      const created: ChecklistItem[] = [];
      for (const text of matched.items) {
        created.push(await tipsApi.createItem(trip.id, section.id, { text }));
      }
      setChecklists(c => c.map(s => s.id === section.id ? { ...s, items: [...s.items, ...created] } : s));
      toast(t('packing.sectionAiAdded', { count: created.length, title: section.title }), 'success');
      setSectionAiOpenId(null);
      return true;
    } catch {
      toast(t('packing.sectionAiFailed'), 'error');
      return false;
    } finally {
      setGeneratingSectionId(null);
    }
  }

  if (loading) return <LoadingScreen />;

  // Counted over the sections on screen: a progress bar mixing your suitcase
  // with everyone else's answers a question nobody asked.
  const totalItems   = visibleSections.reduce((sum, s) => sum + s.items.length, 0);
  const checkedItems = visibleSections.reduce((sum, s) => sum + s.items.filter(i => i.checked).length, 0);
  const checkProgress = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;

  return (
    <div className="flex flex-col h-full overflow-hidden animate-fade-in">
      <div className="shrink-0 px-4 md:px-8 pt-5 pb-4 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-0.5">
            {trip.destination.toUpperCase()}
          </p>
          <h2 className="font-serif italic font-semibold text-2xl md:text-3xl text-slate-900 dark:text-slate-100">
            {t('packing.heading')}
          </h2>
        </div>
      </div>
      </div>{/* /header panel */}
      <div className="flex-1 overflow-auto px-4 md:px-8 py-5 md:py-6">

      <section>
        {isGroup && (
          <PackingScopeBar
            tabs={tabs}
            scope={scope}
            onScopeChange={setScope}
            sharesPacking={sharesPacking}
            onToggleShare={toggleSharing}
            showSharing={scope === SCOPE_MINE}
          />
        )}

        {totalItems > 0 && (
          <p className="text-xs text-slate-400 mb-3">{t('packing.completedProgress', { done: checkedItems, total: totalItems, pct: checkProgress })}</p>
        )}


        {/* Global progress */}
        {totalItems > 0 && (
          <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden mb-5">
            <div
              className="h-full bg-gradient-to-r from-teal-400 to-teal-600 rounded-full transition-all duration-500"
              style={{ width: `${checkProgress}%` }}
            />
          </div>
        )}

        {visibleSections.length === 0 && !readOnly && (
          <PackingSuggestions
            items={defaultSectionNames.map((name, i) => ({ name, meta: DEFAULT_SECTION_META[i] }))}
            onAdd={addSections}
          />
        )}

        {readOnly && (
          <p className="text-xs text-slate-400 dark:text-slate-500 mb-4 flex items-center gap-1.5">
            <Icon as={Lock} size={12} />
            {t('packing.readOnlyHint')}
          </p>
        )}

        {/* Add section — input + action unified into one control so the
            "type a name, then add" flow reads as a single step. */}
        {!readOnly && (
        <div className="mb-4">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <label htmlFor="new-section" className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
              {t('packing.addSectionLabel')}
            </label>
            <button
              onClick={() => setCopyOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 cursor-pointer transition-colors"
            >
              <Icon as={Copy} size={13} />
              {t('packing.copy.button')}
            </button>
          </div>
          <div className="flex items-stretch rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden focus-within:border-teal-400 dark:focus-within:border-teal-500 focus-within:ring-1 focus-within:ring-teal-400/30 transition-all">
            <input
              id="new-section"
              type="text"
              className="flex-1 min-w-0 bg-transparent px-3.5 py-2.5 text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none"
              placeholder={t('packing.newSectionPlaceholder')}
              value={newSectionTitle}
              onChange={e => setNewSectionTitle(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addSection()}
            />
            <button
              onClick={addSection}
              disabled={!newSectionTitle.trim()}
              className="shrink-0 my-1 mr-1 px-4 rounded-xl bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon as={Plus} size={16} />
              {t('packing.addSection')}
            </button>
          </div>
        </div>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visibleSections.map(s => s.id)} strategy={rectSortingStrategy}>
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${aiCollapsed ? 'lg:grid-cols-3' : ''} gap-4`}>
              {visibleSections.map((section, index) => (
                <PackingSectionCard
                  key={section.id}
                  section={section}
                  meta={getSectionMeta(section.title, index, defaultSectionNames)}
                  aiOpen={sectionAiOpenId === section.id}
                  generating={generatingSectionId === section.id}
                  aiBusy={generatingSectionId !== null}
                  onToggleAi={() => setSectionAiOpenId(id => id === section.id ? null : section.id)}
                  onCloseAi={() => setSectionAiOpenId(null)}
                  onGenerate={info => handleSectionAi(section, info)}
                  onDeleteSection={() => deleteSection(section.id)}
                  onToggleItem={(itemId, checked) => toggleItem(section.id, itemId, checked)}
                  onDeleteItem={itemId => deleteItem(section.id, itemId)}
                  onAddItem={text => addItem(section.id, text)}
                  onAssignItem={(itemId, userId) => assignItem(section.id, itemId, userId)}
                  onRenameItem={(itemId, text) => renameItem(section.id, itemId, text)}
                  readOnly={readOnly}
                  members={assignableMembers}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {showOutfits && (
          <OutfitPlanner
            key={outfitOwnerId}
            tripId={trip.id}
            days={days}
            ownerId={outfitOwnerId}
            readOnly={readOnly}
            itemsById={itemsById}
            sections={mySections}
            defaultSectionId={myClothingSection?.id ?? null}
            onCreateGarment={createGarment}
          />
        )}

      </section>

      </div>

      <PackingCopyModal
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        tripId={trip.id}
        scope={newSectionScope}
        myId={myId}
        onCopied={applyCopy}
      />
    </div>
  );
}
